import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { api } from 'src/common/utils/api';
import { CashflowOwner } from '../joint/ownerTypes';

export type LifeEvent = {
  id: string;
  description: string;
  cost: number | null;
  details: string | null;
  eventDate: string | null;
  clientId: string;
  /** Everyone holding a share of the event's cost, the client this was read through among them. */
  owners?: CashflowOwner[];
};

type LifeEventInput = Omit<LifeEvent, 'id' | 'clientId'> & { id?: string };

type UseLifeEventsReturnType = {
  isLoading: boolean;
  isError: boolean;
  /**
   * The read failed with nothing ever loaded, so there are no figures to show. Distinct from
   * `isError`, which a refetch failing over loaded data also sets: that leaves the rows standing
   * and belongs in a banner, not in place of the tab.
   */
  isLoadingError: boolean;
  refetch: () => Promise<unknown>;
  lifeEventsData: LifeEvent[] | undefined;
  upsertLifeEvents: (items: LifeEventInput[]) => Promise<void>;
  deleteLifeEvents: (ids: string[]) => Promise<void>;
};

export default function useLifeEvents(): UseLifeEventsReturnType {
  const { clientId } = useCurrentClient();
  const queryClient = useQueryClient();

  const queryKey = ['clients', clientId, 'life-events'];

  const { data, isLoading, isError, isLoadingError, refetch } = useQuery<LifeEvent[]>({
    queryKey,
  });

  const { mutateAsync: upsertMutation } = useMutation({
    mutationFn: (items: LifeEventInput[]) =>
      api.put(`clients/${clientId}/life-events/batch`, items),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const upsertLifeEvents = async (items: LifeEventInput[]) => {
    await upsertMutation(items);
  };

  const { mutateAsync: deleteMutation } = useMutation({
    mutationFn: (ids: string[]) => api.post(`clients/${clientId}/life-events/delete-request`, ids),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteLifeEvents = async (ids: string[]) => {
    await deleteMutation(ids);
  };

  return {
    isLoading,
    isError,
    isLoadingError,
    refetch,
    lifeEventsData: data,
    upsertLifeEvents,
    deleteLifeEvents,
  };
}
